/**
 * Повторение только что пройденного режима.
 *
 * Экран результата один на все режимы, и до этого он не знал, откуда пришёл:
 * «Ещё одна тренировка» уводила в основную колею. Закончивший «Предложения»
 * получал упражнение на три буквы, и кнопка обещала не то, куда вела, — так
 * об этом и сообщили при живом использовании.
 *
 * Здесь решается два вопроса: чем занимались и можно ли это повторить.
 * Повторить можно не всё: «Основы» кончаются вместе с группами клавиш, «Мои
 * ошибки» — вместе со слабыми местами, режим символов или пакета исчезает,
 * когда весь его набор освоен. Тогда кнопка ведёт в основную колею и говорит
 * об этом.
 */

import type { MessageKey, Translate } from '@type2x/i18n';

import type { PractisedMode } from './app-state.js';

/** Что кнопка сделает и как она называется. */
export interface RepeatAction {
  readonly title: string;
  readonly run: () => void;
}

/** Что нужно знать, чтобы решить, повторимо ли занятие и как его запустить. */
export interface RepeatContext {
  readonly t: Translate;
  readonly onAgain: () => void;
  readonly onStartMistakes: () => void;
  readonly onStartSentences: () => void;
  readonly onStartFundamentals: () => void;
  readonly onStartSymbols: (mode: PractisedSymbol) => void;
  readonly onStartPacks: (mode: PractisedPack) => void;
  readonly onStartLayoutSwitch: () => void;
  readonly onStartTempo: (mode: PractisedTempo) => void;
  readonly onStartCustomText: () => void;
  readonly symbolModes: readonly PractisedSymbol[];
  readonly packModes: readonly PractisedPack[];
  readonly fundamentalsAvailable: boolean;
  readonly mistakesAvailable: boolean;
  /** Освоена ли латиница: без неё пакеты только объясняются, а не открываются. */
  readonly latinReady: boolean;
  /** Освоены ли обе раскладки режима переключения. */
  readonly layoutSwitchReady: boolean;
  /** Ведёт ли основная колея в «Основы», а не в персональную тренировку. */
  readonly resumesToFundamentals: boolean;
}

type PractisedSymbol = Extract<PractisedMode, { kind: 'symbols' }>['mode'];
type PractisedPack = Extract<PractisedMode, { kind: 'packs' }>['mode'];
type PractisedTempo = Extract<PractisedMode, { kind: 'tempo' }>['mode'];

/** Повторимое занятие: название режима и запуск. */
interface Repeatable {
  readonly titleKey: MessageKey;
  readonly run: () => void;
}

/**
 * Осталось ли режиму чему учить.
 *
 * Повторить можно не всё: «Основы» кончаются вместе с группами клавиш, «Мои
 * ошибки» — вместе со слабыми местами, режим символов или пакета исчезает,
 * когда весь его набор освоен, а пакеты и переключение закрываются незнакомой
 * раскладкой.
 *
 * «Свой текст» повторим всегда: он снова спросит текст, а это не тупик, а
 * начало того же режима.
 */
function available(context: RepeatContext, practised: PractisedMode): boolean {
  switch (practised.kind) {
    case 'fundamentals':
      return context.fundamentalsAvailable;
    case 'mistakes':
      return context.mistakesAvailable;
    case 'symbols':
      return context.symbolModes.includes(practised.mode);
    case 'packs':
      return context.latinReady && context.packModes.includes(practised.mode);
    case 'layout-switch':
      return context.layoutSwitchReady;
    default:
      return true;
  }
}

/** Как называется режим и чем он запускается. */
function describe(context: RepeatContext, practised: PractisedMode): Repeatable {
  switch (practised.kind) {
    case 'practice':
      return { titleKey: 'practice.title', run: context.onAgain };
    case 'fundamentals':
      return { titleKey: 'fundamentals.title', run: context.onStartFundamentals };
    case 'mistakes':
      return { titleKey: 'mistakes.title', run: context.onStartMistakes };
    case 'sentences':
      return { titleKey: 'sentences.title', run: context.onStartSentences };
    case 'symbols':
      return {
        titleKey: `symbols.title.${practised.mode}`,
        run: () => context.onStartSymbols(practised.mode),
      };
    case 'packs':
      return {
        titleKey: `packs.title.${practised.mode}`,
        run: () => context.onStartPacks(practised.mode),
      };
    case 'tempo':
      return {
        titleKey: `tempo.title.${practised.mode}`,
        run: () => context.onStartTempo(practised.mode),
      };
    case 'layout-switch':
      return { titleKey: 'layoutSwitch.title', run: context.onStartLayoutSwitch };
    case 'custom-text':
      return { titleKey: 'customText.title', run: context.onStartCustomText };
  }
}

/** Чем повторить занятие; `null` — режиму больше нечему учить. */
function repeatable(context: RepeatContext, practised: PractisedMode): Repeatable | null {
  return available(context, practised) ? describe(context, practised) : null;
}

/** Название основной колеи: «Основы» или персональная тренировка. */
function trackTitleKey(context: RepeatContext): MessageKey {
  return context.resumesToFundamentals ? 'fundamentals.title' : 'practice.title';
}

/**
 * Основное действие экрана результата.
 *
 * Повторимое занятие повторяется; неповторимое уводит в основную колею. И в
 * том и в другом случае кнопка называет режим: «ещё одна тренировка» ничего не
 * обещала, кроме того, что тренировка будет.
 */
export function repeatAction(context: RepeatContext, practised: PractisedMode): RepeatAction {
  const repeat = repeatable(context, practised);

  if (repeat === null) {
    return {
      title: context.t('result.continueTo', { mode: context.t(trackTitleKey(context)) }),
      run: context.onAgain,
    };
  }

  return {
    title: context.t('result.againMode', { mode: context.t(repeat.titleKey) }),
    run: repeat.run,
  };
}

/**
 * Переход в основную колею рядом с повтором; `null` — он ведёт туда же.
 *
 * Из «Предложений» человек должен уметь вернуться к обучению, не заходя в
 * каталог режимов. Но две кнопки, делающие одно и то же, — это вопрос «в чём
 * разница», а не выбор.
 */
export function trackAction(context: RepeatContext, practised: PractisedMode): RepeatAction | null {
  const repeat = repeatable(context, practised);
  if (repeat === null) return null;

  const trackKey = trackTitleKey(context);
  if (repeat.titleKey === trackKey) return null;

  return {
    title: context.t('result.continueTo', { mode: context.t(trackKey) }),
    run: context.onAgain,
  };
}
